import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { deletePosProduct } from '../utils/posUtil';
import { spinnerRing } from '../utils/bookingUtils';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import { useConfig } from '../context/ConfigContext';
import { usePosSession } from '../context/PosSessionContext';
import '../css/Pos.css';

/**
 * Confirms deleting one product.
 *
 * A modal of its own rather than a row of buttons inside the edit form. Deleting
 * is the one irreversible thing that form can do, and inline it sat in the same
 * footer as Cancel and Save changes — three buttons deep in a row, with "Yes,
 * delete" a few pixels from the button that means the opposite. Lifting it out
 * puts the decision on its own screen, names the product being destroyed, and
 * leaves no way to hit it by aiming at something else.
 *
 * COMMITS ON ITS OWN, like PosAddStockModal: the caller is told only that the
 * product is gone, so the form behind it can close without also owning the
 * delete's errors and spinner.
 *
 * Closes on the × or Cancel only — a stray tap on the backdrop must not
 * dismiss a destructive prompt (CLAUDE.md > Behavior).
 *
 * @param {object} props
 * @param {object} props.product - the product to delete; needs `id` and `name`
 * @param {() => void} props.onDeleted - the product is gone; close the form too
 * @param {() => void} props.onClose - dismissed without deleting
 * @returns {JSX.Element}
 */
function PosDeleteProductModal({ product, onDeleted, onClose }) {
  const { clientId } = useConfig();
  const { staffEmail } = usePosSession();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    setError('');
    try {
      await deletePosProduct(clientId, product, staffEmail);
      onDeleted();
    } catch (err) {
      // Stays open on failure: nothing was destroyed, and the reason belongs
      // in front of the person who asked for it rather than behind a form
      // that has already closed.
      setError(describeWriteError(err, { what: 'this product', path: `posTenants/${clientId}/products` }));
      setDeleting(false);
    }
  };

  return createPortal(
    // Stacked above the product form it opens from — see
    // .pos-modal-overlay-stacked.
    <div className="pos-modal-overlay pos-modal-overlay-stacked">
      <div
        className="pos-modal pos-modal-narrow"
        role="dialog"
        aria-modal="true"
        aria-label={`Delete ${product?.name || 'product'}`}
      >
        <header className="pos-modal-head">
          <h3>Delete product</h3>
          <button type="button" className="pos-modal-close" onClick={onClose} aria-label="Close" disabled={deleting}>
            <X size={18} />
          </button>
        </header>

        <div className="pos-modal-body">
          {/* Names the product. "Delete this product?" on its own is the kind
              of prompt people confirm without reading. */}
          <p className="pos-delete-lead">
            Delete <strong>{product?.name}</strong>?
          </p>
          <p className="pos-delete-note">
            This removes it from the till and from Inventory. Past sales keep their own
            record of it, so receipts and reports already taken are unaffected.
          </p>

          {error && <p className="pos-error">{error}</p>}
          {deleting && <div className="pos-modal-saving">{spinnerRing('Deleting…')}</div>}
        </div>

        <footer className="pos-modal-foot">
          <span className="pos-foot-spacer" />
          <button type="button" className="pos-btn pos-btn-ghost" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button type="button" className="pos-btn pos-btn-danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete product'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default PosDeleteProductModal;
